import styled from "styled-components"
import Text from "./components/Text"
import globalStyles from "@data/style.globals"
import ScreenImages from "./components/ScreenImages"
import useStore from "@state/store"
import TCanvas from "./canvas-components/TCanvas"
import FullScreenImage from "./components/FullScreenImage"
import { useSyncStatesWithSocket } from "@utils/useSyncStatesWithSockets"
import { useGSAP } from "@gsap/react"
import { gsap } from "gsap"
import FocusText from "./components/FocusText"
import MeshSelector from "@pages/tablet/amenities/components/MeshSelector"
import { useEffect } from "react"
import { useScreenTimeout } from "@hooks/useScreenTimeout"
import { useAmenitiesStateGuard } from "@pages/tablet/AmenitiesStateGuard"
import { useCoordinatedLoading } from "@hooks/useCoordinatedLoading"

type Props = {}

export function Amenities(props: Props) {
  // useAmenitiesStateGuard()
  const amenitiesSelector = useStore((state) => state.amenitiesSelector)
  const amenitiesActiveMesh = useStore((state) => state.amenitiesActiveMesh)
  const amenitiesActiveImage = useStore((state) => state.amenitiesActiveImage)
  const amenitiesFullScreenImage = useStore(
    (state) => state.amenitiesFullScreenImage,
  )
  const showAmenitiesScreenSaver = useStore(
    (state) => state.showAmenitiesScreenSaver,
  )

  const loadProgress = useStore((state) => state.loadProgress)
  const loaded = useCoordinatedLoading("screen")
  const { syncStatesWithSocket: syncState } = useSyncStatesWithSocket()

  useGSAP(() => {
    if (loadProgress >= 100) {
      setTimeout(() => {
        gsap.to("#loading", { autoAlpha: 0, duration: 1 })
        syncState({ loaded: true })
      }, 100)
    }
  }, [loadProgress])

  useGSAP(
    () => {
      if (!loaded) return

      // Add immediate visibility control
      const sidebarElements = ["#sidebar-text-div", ".image-gallery"]
      const focusElements = [
        "#amenities-text",
        "#amenities-selector",
        "#amenity-close",
      ]

      // Immediately set visibility before animation
      gsap.set(sidebarElements, {
        visibility: amenitiesSelector === null ? "visible" : "hidden",
      })
      gsap.set(focusElements, {
        visibility: amenitiesSelector !== null ? "visible" : "hidden",
      })

      // Existing animations
      gsap.to(sidebarElements, {
        duration: 0.3,
        autoAlpha: amenitiesSelector === null ? 1 : 0,
        y: amenitiesSelector === null ? 0 : 300,
        delay: amenitiesSelector === null ? 0.3 : 0,
      })
      gsap.to(["#sidebar-selector"], {
        duration: 0.3,
        autoAlpha: amenitiesSelector === null ? 1 : 0,
        y: amenitiesSelector === null ? 0 : 300,
        delay: amenitiesSelector === null ? 1 : 0,
      })
      gsap.to(focusElements, {
        duration: 0.3,
        autoAlpha: amenitiesSelector !== null ? 1 : 0,
        y: amenitiesSelector !== null ? 0 : 300,
        delay: amenitiesSelector === null ? 0 : 0.3,
      })
    },
    { dependencies: [amenitiesSelector, loaded] },
  )

  useEffect(() => {
    syncState({
      amenitiesActiveMesh: null,
      amenitiesActiveImage: null,
      amenitiesFullScreenImage: null,
      amenitiesSelector: null,
    })

    return () => {
      syncState({
        amenitiesActiveMesh: null,
        amenitiesActiveImage: null,
        amenitiesFullScreenImage: null,
        amenitiesSelector: null,
      })
    }
  }, [])

  const showScreenSaver = useScreenTimeout(5 * 60 * 1000, [
    amenitiesSelector,
    amenitiesActiveImage,
    amenitiesActiveMesh,
    amenitiesFullScreenImage,
    showAmenitiesScreenSaver,
  ])

  return (
    <Wrapper
      style={{
        backgroundColor: globalStyles.colors.offBlack,
        color: "white",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gridTemplateRows: "repeat(2, minmax(0, 1fr))",
        zIndex: 3,
      }}
    >
      <video
        style={{
          opacity: showScreenSaver ? 1 : 0,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "100%",
          zIndex: 100,
          objectFit: "cover",
          transition: "opacity 0.5s",
        }}
        loop
        autoPlay
        muted
      >
        <source src="/assets/videos/amenities.mp4" />
      </video>
      <video
        style={{
          opacity: showAmenitiesScreenSaver ? 1 : 0,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "100%",
          zIndex: 101,
          objectFit: "cover",
          transition: "opacity 0.5s",
        }}
        loop
        autoPlay
        muted
      >
        <source src="/assets/videos/amenities.mp4" />
      </video>
      <div
        id="loading"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20,
          height: "100%",
          width: "100%",
          display: "grid",
          backgroundColor: globalStyles.colors.offBlack,
          placeItems: "center",
        }}
      >
        <p style={{ fontSize: "2rem" }}>
          LOADING AMENITIES... {loadProgress.toFixed(0)}%
        </p>
      </div>
      {loaded && (
        <>
          <Text screen="tv" />
          <ScreenImages screen="tv" />
          <FullScreenImage $screen="tv" />
          <FocusText screen="tv" />
          <MeshSelector screen="tv" />
        </>
      )}
      <TCanvas screen="tv" />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`
